import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { reportError } from "@/lib/errors";

/**
 * Global ErrorBoundary — the safety net. If any descendant component throws
 * during render (e.g. an unexpected response shape, a missing prop, a
 * race-condition state) the boundary swaps in a calm, on-brand fallback
 * instead of the red "Uncaught runtime errors" overlay shown by CRA.
 *
 * Logs the error + componentStack to the console so devs can debug, but
 * never exposes it to the user. The fallback gives the user two clear
 * recovery paths: reload, or go home.
 *
 * Usage (already wired in App.js):
 *     <ErrorBoundary><Routes>...</Routes></ErrorBoundary>
 */
export default class ErrorBoundary extends React.Component {
  state = { hasError: false, message: "" };

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: (error && error.message) ? String(error.message) : "Unexpected error",
    };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    // Pipe to the operator's admin inbox so they see render crashes in
    // real time, even before users report them.
    try {
      reportError(error, {
        severity: "critical",
        component: "ErrorBoundary",
        extra: { componentStack: info?.componentStack?.slice(0, 4000) || "" },
      });
    } catch {
      /* logging never breaks the fallback UI */
    }
  }

  reset = () => {
    this.setState({ hasError: false, message: "" });
  };

  reload = () => {
    window.location.reload();
  };

  goHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen bg-black text-white flex items-center justify-center px-6"
      >
        <div className="max-w-lg w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/40 mb-5">
            <AlertTriangle className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-3xl text-gold font-display mb-2">Something went sideways</h1>
          <p className="text-sm text-white/60 mb-6">
            We hit a hiccup loading this page. Your data is safe — refresh and you'll be right back where you were.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <button
              data-testid="error-reload"
              onClick={this.reload}
              className="inline-flex items-center justify-center gap-2 bg-gold text-black hover:bg-gold/90 rounded-md px-5 py-2.5 text-sm font-semibold transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Reload
            </button>
            <button
              data-testid="error-home"
              onClick={this.goHome}
              className="inline-flex items-center justify-center gap-2 text-white/70 hover:text-gold border border-white/15 hover:border-gold/40 rounded-md px-5 py-2.5 text-sm transition-colors"
            >
              <Home className="w-4 h-4" /> Go home
            </button>
          </div>
          {process.env.NODE_ENV !== "production" && this.state.message && (
            <pre className="mt-6 text-xs text-rose-300/80 text-left bg-black/40 border border-white/10 rounded-md p-3 overflow-auto max-h-48">
              {this.state.message}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
